import { useCallback, MouseEvent, useState, useEffect } from "react";

import { useIntl } from "react-intl";

import { ConfluenceLogo } from "@atlaskit/logo";
import UFOSegment from "@atlaskit/react-ufo/segment";
import { Editions, ProductKeys } from "@atlassiansox/asc-stargate";

import { useCrossFlowFrontendTouchpoint } from "@/components/cross-flow/touchpoint";
import { useSiteSelectorContext } from "@/components/site-selector-modal";
import { useLocalizedHref } from "@/libraries/web-framework";
import { ProductPageSubNav } from "@/patterns/global-nav/src";
import { useConfluenceTemplateExperimentContext } from "@/templates/software/confluence/templates/confluence-templates-page/src/sections/shared-header-footer-container";
import { experiments, useExperiment } from "@/utils/experiment";
import { useLoggedInUser } from "@/utils/logged-in-user-provider";

type ConfluenceSoftwareNavProps = {
    isWrappedInSharedHeaderFooterContainer?: boolean;
};

const BaseConfluenceSoftwareNav = () => {
    const intl = useIntl();

    const signupLink = useLocalizedHref(
        "/try/cloud/signup?bundle=confluence&edition=free"
    );
    const { openCrossFlow } = useCrossFlowFrontendTouchpoint({
        productKey: ProductKeys.CONFLUENCE,
        edition: Editions.FREE,
        redirectUrl: signupLink
    });

    return (
        <UFOSegment name="confluence-pt-cfind-1234-confluenceSoftwareNav">
            <ProductPageSubNav
                productLogoUrl={useLocalizedHref("/software/confluence")}
                productLogo={<ConfluenceLogo appearance="brand" />}
                productLogoAriaLabel={intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.navbar.confluence-logo",
                    defaultMessage: "Confluence logo"
                })}
                mobileCtaText={intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.navbar.getitfree",
                    defaultMessage: "Get it free"
                })}
                navigationTrynowCtaText={intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.navbar.getitfree",
                    defaultMessage: "Get it free"
                })}
                mobileCtaUrl={signupLink}
                mobileCtaOnClick={openCrossFlow}
                mobileLogo={<ConfluenceLogo appearance="brand" />}
                productPageNavLinks={[
                    {
                        globalLinksKey: "features",
                        linkText: intl.formatMessage({
                            id: "ConfluenceSoftwarePageTemplate.navbar.features",
                            defaultMessage: "Features"
                        }),
                        hasSubnav: false,
                        productKey: "confluence",
                        href: useLocalizedHref("/software/confluence/features")
                    },
                    {
                        globalLinksKey: "resources",
                        linkText: intl.formatMessage({
                            id: "ConfluenceSoftwarePageTemplate.navbar.resources",
                            defaultMessage: "Resources"
                        }),
                        hasSubnav: false,
                        productKey: "confluence",
                        href: useLocalizedHref("/software/confluence/resources")
                    },
                    {
                        globalLinksKey: "templates",
                        linkText: intl.formatMessage({
                            id: "ConfluenceSoftwarePageTemplate.navbar.templates",
                            defaultMessage: "Templates"
                        }),
                        hasSubnav: false,
                        productKey: "confluence",
                        href: useLocalizedHref("/software/confluence/templates")
                    },
                    {
                        globalLinksKey: "pricing",
                        linkText: intl.formatMessage({
                            id: "ConfluenceSoftwarePageTemplate.navbar.pricing",
                            defaultMessage: "Pricing"
                        }),
                        hasSubnav: false,
                        productKey: "confluence",
                        href: useLocalizedHref("/software/confluence/pricing")
                    },
                    {
                        globalLinksKey: "enterprise",
                        linkText: intl.formatMessage({
                            id: "ConfluenceSoftwarePageTemplate.navbar.enterprise",
                            defaultMessage: "Enterprise"
                        }),
                        hasSubnav: false,
                        productKey: "confluence",
                        href: useLocalizedHref(
                            "/software/confluence/enterprise"
                        )
                    }
                ]}
                navigationTrynowCtaUrl={signupLink}
                navigationTrynowCtaOnClick={openCrossFlow}
                mobileLinksSections={[
                    {
                        mobileLinkKey: "mobile-confluence",
                        mobileLinksProps: [
                            {
                                globalLinksKey: "features",
                                linkText: intl.formatMessage({
                                    id: "ConfluenceSoftwarePageTemplate.navbar.features",
                                    defaultMessage: "Features"
                                }),
                                hasSubnav: false,
                                productKey: "confluence",
                                href: useLocalizedHref(
                                    "/software/confluence/features"
                                )
                            },
                            {
                                globalLinksKey: "resources",
                                linkText: intl.formatMessage({
                                    id: "ConfluenceSoftwarePageTemplate.navbar.resources",
                                    defaultMessage: "Resources"
                                }),
                                hasSubnav: false,
                                productKey: "confluence",
                                href: useLocalizedHref(
                                    "/software/confluence/resources"
                                )
                            },
                            {
                                globalLinksKey: "templates",
                                linkText: intl.formatMessage({
                                    id: "ConfluenceSoftwarePageTemplate.navbar.templates",
                                    defaultMessage: "Templates"
                                }),
                                hasSubnav: false,
                                productKey: "confluence",
                                href: useLocalizedHref(
                                    "/software/confluence/templates"
                                )
                            },
                            {
                                globalLinksKey: "pricing",
                                linkText: intl.formatMessage({
                                    id: "ConfluenceSoftwarePageTemplate.navbar.pricing",
                                    defaultMessage: "Pricing"
                                }),
                                hasSubnav: false,
                                productKey: "confluence",
                                href: useLocalizedHref(
                                    "/software/confluence/pricing"
                                )
                            },
                            {
                                globalLinksKey: "enterprise",
                                linkText: intl.formatMessage({
                                    id: "ConfluenceSoftwarePageTemplate.navbar.enterprise",
                                    defaultMessage: "Enterprise"
                                }),
                                hasSubnav: false,
                                productKey: "confluence",
                                href: useLocalizedHref(
                                    "/software/confluence/enterprise"
                                )
                            }
                        ]
                    }
                ]}
            />
        </UFOSegment>
    );
};

const WrappedConfluenceSoftwareNav = ({
    isWrappedInSharedHeaderFooterContainer = false
}: ConfluenceSoftwareNavProps) => {
    const intl = useIntl();
    const siteSelectorContext = useSiteSelectorContext();
    const confluenceTemplateExperimentContext =
        useConfluenceTemplateExperimentContext();
    const { isUserLoggedIn, requestInProgress } = useLoggedInUser();
    const [getItFreeTextLabel, setGetItFreeTextLabel] = useState("");
    const [isDisplayGetitFree, setIsDisplayGetitFree] = useState(false);

    const openModalForTemplate = isWrappedInSharedHeaderFooterContainer
        ? siteSelectorContext?.openModalForTemplate
        : undefined;
    const template = isWrappedInSharedHeaderFooterContainer
        ? confluenceTemplateExperimentContext?.template
        : undefined;

    const signupLink = useLocalizedHref(
        "/try/cloud/signup?bundle=confluence&edition=free"
    );
    const { openCrossFlow } = useCrossFlowFrontendTouchpoint({
        productKey: ProductKeys.CONFLUENCE,
        edition: Editions.FREE,
        redirectUrl: signupLink
    });
    const getItFreeTextLabelDefault = intl.formatMessage({
        id: "ConfluenceSoftwarePageTemplate.navbar.getitfree",
        defaultMessage: "Get it free"
    });

    // Experiment code ================================
    const confluenceTemplatesProductNavCtaExperiment = useExperiment(
        experiments.gwp6441ConfluenceTemplatesProductNavCta
    );
    const isInConfluenceTemplatesProductNavCtaExperiment =
        confluenceTemplatesProductNavCtaExperiment.type === "enrolled" &&
        confluenceTemplatesProductNavCtaExperiment.value === true;

    const useTemplateTextLabel = intl.formatMessage({
        id: "ConfluenceSoftwarePageTemplate.navbar.useTemplate",
        defaultMessage: "Use template"
    });

    const navigateToSignup = useCallback(
        (event: MouseEvent<Element>) => {
            event.stopPropagation();
            event.preventDefault();

            if (openModalForTemplate) {
                openModalForTemplate(
                    {
                        templateId: template?.pluginModuleKey || "",
                        templateName: template?.templateNameExternal ?? "",
                        productKey: ProductKeys.CONFLUENCE
                    },
                    true
                );
            }
        },
        [openModalForTemplate, template]
    );

    const isExperimentEnabled =
        isInConfluenceTemplatesProductNavCtaExperiment &&
        isUserLoggedIn &&
        template;
    const getItFreeCtaOnClick = isExperimentEnabled
        ? navigateToSignup
        : openCrossFlow;

    useEffect(() => {
        if (!requestInProgress) {
            if (isExperimentEnabled && template) {
                setGetItFreeTextLabel(useTemplateTextLabel);
            } else {
                setGetItFreeTextLabel(getItFreeTextLabelDefault);
            }
        }
    }, [requestInProgress, isUserLoggedIn, template]);

    useEffect(() => {
        if (!getItFreeTextLabel) {
            // settimeout here to prevent blink effect of CTA
            setTimeout(() => {
                setIsDisplayGetitFree(true);
            }, 1000);
        }
    }, [getItFreeTextLabel]);
    // ================================================

    return (
        <UFOSegment name="confluence-pt-cfind-1234-confluenceSoftwareNav">
            <ProductPageSubNav
                productLogoUrl={useLocalizedHref("/software/confluence")}
                productLogo={<ConfluenceLogo appearance="brand" />}
                productLogoAriaLabel={intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.navbar.confluence-logo",
                    defaultMessage: "Confluence logo"
                })}
                mobileCtaText={getItFreeTextLabel}
                navigationTrynowCtaText={getItFreeTextLabel}
                mobileCtaUrl={signupLink}
                mobileCtaOnClick={getItFreeCtaOnClick}
                mobileLogo={<ConfluenceLogo appearance="brand" />}
                isShowTryNowLink={isDisplayGetitFree}
                productPageNavLinks={[
                    {
                        globalLinksKey: "features",
                        linkText: intl.formatMessage({
                            id: "ConfluenceSoftwarePageTemplate.navbar.features",
                            defaultMessage: "Features"
                        }),
                        hasSubnav: false,
                        productKey: "confluence",
                        href: useLocalizedHref("/software/confluence/features")
                    },
                    {
                        globalLinksKey: "resources",
                        linkText: intl.formatMessage({
                            id: "ConfluenceSoftwarePageTemplate.navbar.resources",
                            defaultMessage: "Resources"
                        }),
                        hasSubnav: false,
                        productKey: "confluence",
                        href: useLocalizedHref("/software/confluence/resources")
                    },
                    {
                        globalLinksKey: "templates",
                        linkText: intl.formatMessage({
                            id: "ConfluenceSoftwarePageTemplate.navbar.templates",
                            defaultMessage: "Templates"
                        }),
                        hasSubnav: false,
                        productKey: "confluence",
                        href: useLocalizedHref("/software/confluence/templates")
                    },
                    {
                        globalLinksKey: "pricing",
                        linkText: intl.formatMessage({
                            id: "ConfluenceSoftwarePageTemplate.navbar.pricing",
                            defaultMessage: "Pricing"
                        }),
                        hasSubnav: false,
                        productKey: "confluence",
                        href: useLocalizedHref("/software/confluence/pricing")
                    },
                    {
                        globalLinksKey: "enterprise",
                        linkText: intl.formatMessage({
                            id: "ConfluenceSoftwarePageTemplate.navbar.enterprise",
                            defaultMessage: "Enterprise"
                        }),
                        hasSubnav: false,
                        productKey: "confluence",
                        href: useLocalizedHref(
                            "/software/confluence/enterprise"
                        )
                    }
                ]}
                navigationTrynowCtaUrl={signupLink}
                navigationTrynowCtaOnClick={getItFreeCtaOnClick}
                mobileLinksSections={[
                    {
                        mobileLinkKey: "mobile-confluence",
                        mobileLinksProps: [
                            {
                                globalLinksKey: "features",
                                linkText: intl.formatMessage({
                                    id: "ConfluenceSoftwarePageTemplate.navbar.features",
                                    defaultMessage: "Features"
                                }),
                                hasSubnav: false,
                                productKey: "confluence",
                                href: useLocalizedHref(
                                    "/software/confluence/features"
                                )
                            },
                            {
                                globalLinksKey: "resources",
                                linkText: intl.formatMessage({
                                    id: "ConfluenceSoftwarePageTemplate.navbar.resources",
                                    defaultMessage: "Resources"
                                }),
                                hasSubnav: false,
                                productKey: "confluence",
                                href: useLocalizedHref(
                                    "/software/confluence/resources"
                                )
                            },
                            {
                                globalLinksKey: "templates",
                                linkText: intl.formatMessage({
                                    id: "ConfluenceSoftwarePageTemplate.navbar.templates",
                                    defaultMessage: "Templates"
                                }),
                                hasSubnav: false,
                                productKey: "confluence",
                                href: useLocalizedHref(
                                    "/software/confluence/templates"
                                )
                            },
                            {
                                globalLinksKey: "pricing",
                                linkText: intl.formatMessage({
                                    id: "ConfluenceSoftwarePageTemplate.navbar.pricing",
                                    defaultMessage: "Pricing"
                                }),
                                hasSubnav: false,
                                productKey: "confluence",
                                href: useLocalizedHref(
                                    "/software/confluence/pricing"
                                )
                            },
                            {
                                globalLinksKey: "enterprise",
                                linkText: intl.formatMessage({
                                    id: "ConfluenceSoftwarePageTemplate.navbar.enterprise",
                                    defaultMessage: "Enterprise"
                                }),
                                hasSubnav: false,
                                productKey: "confluence",
                                href: useLocalizedHref(
                                    "/software/confluence/enterprise"
                                )
                            }
                        ]
                    }
                ]}
            />
        </UFOSegment>
    );
};

const ConfluenceSoftwareNav = ({
    isWrappedInSharedHeaderFooterContainer = false
}: ConfluenceSoftwareNavProps) => {
    if (isWrappedInSharedHeaderFooterContainer) {
        return (
            <WrappedConfluenceSoftwareNav
                isWrappedInSharedHeaderFooterContainer={
                    isWrappedInSharedHeaderFooterContainer
                }
            />
        );
    }

    return <BaseConfluenceSoftwareNav />;
};

ConfluenceSoftwareNav.displayName = "ConfluenceSoftwareNav";

export default ConfluenceSoftwareNav;
